/**
 * CHAT-02a — conversations find-or-create helper.
 *
 * Backs the flag-gated buyer→agent contact CTA. Given a listing + agent, it
 * returns the id of the buyer's existing conversation for that pair, or creates
 * one if none exists. The buyer is always the current session user.
 *
 * Invariants (locked by the CHAT-02a contract):
 *  - NEVER mutates an existing conversation. We SELECT by the natural key first
 *    and only INSERT when nothing is found. We deliberately do NOT use a default
 *    upsert (ON CONFLICT DO UPDATE) because that would silently overwrite the
 *    existing row's columns.
 *  - The conversations row has DB defaults for everything except the three keys,
 *    so we insert ONLY { listing_id, agent_id, buyer_id }.
 *  - On a concurrent-insert race the UNIQUE constraint
 *    (listing_id, agent_id, buyer_id) raises Postgres 23505; we recover by
 *    re-selecting and returning the existing id.
 */

import { apiGet, apiPost } from '../../lib/api/client';
import { getValidAccessToken, getCurrentUser } from '../../lib/api/authApi';

interface DbConversationId {
  id: string;
}

export interface FindOrCreateConversationParams {
  listingId: string;
  agentId: string;
}

/**
 * Select the existing conversation id for (listing, agent, buyer), or null.
 */
async function selectConversationId(
  listingId: string,
  agentId: string,
  buyerId: string,
  accessToken: string
): Promise<string | null> {
  const { data } = await apiGet<DbConversationId[]>(
    'conversations',
    `listing_id=eq.${listingId}&agent_id=eq.${agentId}&buyer_id=eq.${buyerId}&select=id&limit=1`,
    accessToken
  );
  return data && data.length > 0 ? data[0].id : null;
}

/**
 * Returns the id of the buyer's conversation for this listing+agent, creating
 * it if necessary. Requires an authenticated session (the caller guarantees it);
 * the buyer_id is taken from the current session, never from arguments.
 */
export async function findOrCreateConversation({
  listingId,
  agentId,
}: FindOrCreateConversationParams): Promise<string> {
  const accessToken = await getValidAccessToken();
  const buyerId = getCurrentUser()?.id;

  if (!accessToken || !buyerId) {
    throw new Error('You must be signed in to start a conversation.');
  }

  // 1. Find-first: return the existing conversation without touching it.
  const existing = await selectConversationId(listingId, agentId, buyerId, accessToken);
  if (existing) return existing;

  // 2. None found — insert only the natural key; all other columns use DB
  //    defaults. NOT an upsert, so an existing row is never mutated.
  const { data, error } = await apiPost<DbConversationId[]>(
    'conversations',
    { listing_id: listingId, agent_id: agentId, buyer_id: buyerId },
    accessToken
  );

  if (!error && data && data.length > 0) {
    return data[0].id;
  }

  // 3. Lost the insert race (UNIQUE constraint → Postgres 23505): the row now
  //    exists, so re-select and return it.
  if (error?.code === '23505') {
    const raced = await selectConversationId(listingId, agentId, buyerId, accessToken);
    if (raced) return raced;
  }

  throw new Error(error?.message || 'Failed to start conversation.');
}
